import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';

const ToastContext = createContext(null);

let _nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = _nextId++;
    setToasts(prev => [...prev, { id, message, type }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  // `toast` is a hybrid: callable as toast(msg, type) AND has .success/.error/etc.
  // This supports both calling conventions used across the codebase.
  const toast = useMemo(() => {
    const fn = (msg, type = 'info', duration) => addToast(msg, type, duration);
    fn.success = (msg, duration) => addToast(msg, 'success', duration);
    fn.error   = (msg, duration) => addToast(msg, 'error',   duration ?? 6000);
    fn.info    = (msg, duration) => addToast(msg, 'info',    duration);
    fn.warning = (msg, duration) => addToast(msg, 'warning', duration);
    return fn;
  }, [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

const ICONS = {
  success: '✓',
  error:   '✕',
  warning: '⚠',
  info:    'ℹ',
};

const COLORS = {
  success: { bg: '#d1fae5', border: '#34d399', text: '#065f46' },
  error:   { bg: '#fee2e2', border: '#f87171', text: '#991b1b' },
  warning: { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' },
  info:    { bg: '#dbeafe', border: '#60a5fa', text: '#1e40af' },
};

function ToastContainer({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '1.5rem',
      right: '1.5rem',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      maxWidth: '360px',
      width: '100%',
    }}>
      {toasts.map(t => {
        const c = COLORS[t.type] || COLORS.info;
        return (
          <div key={t.id} role="alert" style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.6rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: `1px solid ${c.border}`,
            background: c.bg,
            color: c.text,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            fontSize: '0.875rem',
            lineHeight: '1.4',
            animation: 'ams-toast-in 0.2s ease',
          }}>
            <span style={{ fontWeight: 700, flexShrink: 0, fontSize: '0.9rem' }}>
              {ICONS[t.type]}
            </span>
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: c.text,
                opacity: 0.6,
                padding: '0 0 0 0.25rem',
                fontSize: '1rem',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >×</button>
          </div>
        );
      })}
      <style>{`
        @keyframes ams-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
